import * as prismic from '@prismicio/client';
import * as prismicNext from '@prismicio/next';
import config from '../prismic.config.json';

type Simplify<T> = { [KeyType in keyof T]: T[KeyType] };

/**
 * The project's Prismic repository name.
 */
export const repositoryName =
  process.env.NEXT_PUBLIC_PRISMIC_ENVIRONMENT || config.repositoryName;

/**
 * A list of Route Resolver objects that define how a document's `url` field is resolved.
 *
 * {@link https://prismic.io/docs/route-resolver#route-resolver}
 */
// TODO: Update the routes array to match your project's route structure.
const routes: prismic.ClientConfig['routes'] = [
  // Examples:
  {
    type: 'page',
    path: '/:uid',
  },
  {
    type: 'blog',
    path: '/blog',
  },
  { type: 'form', path: '/form' },
];

/**
 * Creates a Prismic client for the project's repository. The client is used to
 * query content from the Prismic API.
 *
 * @param config - Configuration for the Prismic client.
 */
export const createClient = () => {
  const client = prismic.createClient(repositoryName, {
    routes,
    fetchOptions: { next: { tags: ['prismic'] }, cache: 'force-cache' },
    accessToken: process.env.NEXT_PUBLIC_RESERVE_MY_LOAN_API_KEY,
  });

  prismicNext.enableAutoPreviews({
    client,
  });

  return client;
};

/**
 * Item in *TriPointModal → Default → Primary → Blocks*
 */
export interface TriPointModalSliceDefaultPrimaryBlocksItem {
  /**
   * title field in *TriPointModal → Default → Primary → Blocks*
   *
   * - **Field Type**: Rich Text
   * - **Placeholder**: *None*
   * - **API ID Path**: tri_point_modal.default.primary.blocks[].title
   * - **Documentation**: https://prismic.io/docs/field#rich-text-title
   */
  title: prismic.RichTextField;

  /**
   * text field in *TriPointModal → Default → Primary → Blocks*
   *
   * - **Field Type**: Rich Text
   * - **Placeholder**: *None*
   * - **API ID Path**: tri_point_modal.default.primary.blocks[].text
   * - **Documentation**: https://prismic.io/docs/field#rich-text-title
   */
  text: prismic.RichTextField;
}

/**
 * Primary content in *TriPointModal → Default → Primary*
 */
export interface TriPointModalSliceDefaultPrimary {
  /**
   * LastUpdateLine field in *TriPointModal → Default → Primary*
   *
   * - **Field Type**: Rich Text
   * - **Placeholder**: *None*
   * - **API ID Path**: tri_point_modal.default.primary.lastupdateline
   * - **Documentation**: https://prismic.io/docs/field#rich-text-title
   */
  lastupdateline: prismic.RichTextField;

  /**
   * Blocks field in *TriPointModal → Default → Primary*
   *
   * - **Field Type**: Group
   * - **Placeholder**: *None*
   * - **API ID Path**: tri_point_modal.default.primary.blocks[]
   * - **Documentation**: https://prismic.io/docs/field#group
   */
  blocks: prismic.GroupField<
    Simplify<TriPointModalSliceDefaultPrimaryBlocksItem>
  >;
}

/**
 * Default variation for TriPointModal Slice
 *
 * - **API ID**: `default`
 * - **Description**: Default
 * - **Documentation**: https://prismic.io/docs/slice
 */
export type TriPointModalSliceDefault = prismic.SharedSliceVariation<
  'default',
  Simplify<TriPointModalSliceDefaultPrimary>,
  never
>;

/**
 * Slice variation for *TriPointModal*
 */
type TriPointModalSliceVariation = TriPointModalSliceDefault;

/**
 * TriPointModal Shared Slice
 *
 * - **API ID**: `tri_point_modal`
 * - **Description**: TriPointModal
 * - **Documentation**: https://prismic.io/docs/slice
 */
export type TriPointModalSlice = prismic.SharedSlice<
  'tri_point_modal',
  TriPointModalSliceVariation
>;
